import React, { useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { trpc } from '@/lib/trpc';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Drawer,
  Pagination,
  Spinner,
  toast,
  useOverlayState,
} from '@heroui/react';
import { ItemCard, Segment } from '@heroui-pro/react';
import { IconChevronRight as ChevronRight, IconX as X } from '@tabler/icons-react';
import { NavBar } from '@/components/layout';
import { useAppSelector } from '@/stores/hooks';
import { formatDateTime } from '@his/shared/utils/formatTime';
import { useClipboard } from '@his/shared';
import QRCode from 'qrcode';
import postbgUrl from '@/assets/images/postbg.webp';
import defaultAvatarUrl from '@/assets/images/default-avatar.webp';

/** 将 extra 对象格式化为键值对字符串，如 "key1:value1 key2:value2" */
function formatExtra(extra: Record<string, unknown> | null | undefined): string {
  if (!extra || typeof extra !== 'object') return '';
  return Object.entries(extra)
    .map(([k, v]) => `${k}:${v}`)
    .join(' ');
}

/** 列表标题：姓名 + 单空格 + 先生/女士 */
function nameSpaceGenderTitle(name: string | null | undefined, gender: string | null | undefined): string {
  const n = (name ?? '').trim();
  if (!n) return '-';
  const suffix = gender === 'MALE' ? '先生' : gender === 'FEMALE' ? '女士' : '';
  return suffix ? `${n} ${suffix}` : n;
}

/** 姓名首字（按 Unicode 字素），用于 ItemCard.Icon */
function nameFirstGrapheme(name: string | null | undefined): string {
  const n = (name ?? '').trim();
  if (!n) return '—';
  const chars = Array.from(n);
  return chars[0] ?? '—';
}

/** 分页页码条（含省略号），与桌面端表格分页逻辑一致 */
function buildPaginationPages(totalPages: number, safePage: number): (number | 'ellipsis')[] {
  const pages: (number | 'ellipsis')[] = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (safePage > 3) pages.push('ellipsis');
    const start = Math.max(2, safePage - 1);
    const end = Math.min(totalPages - 1, safePage + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (safePage < totalPages - 2) pages.push('ellipsis');
    pages.push(totalPages);
  }
  return pages;
}

/** 姓名首字 + 性别（先生/女士），用于喜报大标题 */
function nameFirstCharWithGender(name: string | null | undefined, gender: string | null | undefined): string {
  const first = (name ?? '').trim()[0];
  if (!first) return '—';
  const suffix = gender === 'MALE' ? '先生' : gender === 'FEMALE' ? '女士' : '';
  return first + suffix;
}

/** 手机号第 4～7 位转为 * */
function maskMobile(mobile: string | null | undefined): string {
  const s = (mobile ?? '').trim();
  if (s.length < 8) return s;
  return s.slice(0, 3) + '****' + s.slice(7);
}

/** 海报用时间格式：YYYY年M月D日 HH:mm */
function formatPosterWonAt(input: Date | string | null | undefined): string {
  if (!input) return '-';
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return '-';
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${y}年${m}月${day}日 ${hh}:${mm}`;
}

/** 活动标题超过 maxChars 时追加省略号 */
function truncatePosterTitle(title: string | null | undefined, maxChars = 18): string {
  const raw = (title ?? '').trim();
  if (!raw) return '-';
  const chars = Array.from(raw);
  if (chars.length <= maxChars) return raw;
  return `${chars.slice(0, maxChars).join('')}…`;
}

const POSTER_WIDTH = 1080;
const POSTER_HEIGHT = 1920;

/** 加载跨域图片供 canvas 绘制：先 Image + crossOrigin，失败则 fetch + blob URL。 */
async function loadCrossOriginImage(url: string): Promise<HTMLImageElement> {
  const trimmed = url.trim();
  const tryImage = (): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.referrerPolicy = 'no-referrer';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`图片加载失败: ${trimmed.slice(0, 60)}…`));
      img.src = trimmed;
    });

  const tryFetchBlob = async (): Promise<HTMLImageElement> => {
    const res = await fetch(trimmed, { mode: 'cors', referrerPolicy: 'no-referrer' });
    if (!res.ok) throw new Error(`图片请求 ${res.status}: ${trimmed.slice(0, 50)}…`);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(blobUrl);
        resolve(img);
      };
      img.onerror = () => {
        URL.revokeObjectURL(blobUrl);
        reject(new Error(`图片解码失败: ${trimmed.slice(0, 50)}…`));
      };
      img.src = blobUrl;
    });
  };

  try {
    return await tryImage();
  } catch {
    return await tryFetchBlob();
  }
}

type InvitationItem = {
  id: string;
  name?: string | null;
  gender?: string | null;
  mobile?: string | null;
  extra?: Record<string, unknown> | null;
  registeredAt?: Date | string | null;
  createdAt?: Date | string | null;
  status?: string | null;
  isWinner?: boolean | null;
  prizeId?: string | null;
  wonAt?: Date | string | null;
  prize?: { id?: string; name?: string; imageUrl?: string | null } | null;
};

/**
 * 我的邀请页面
 *
 * 路由模式：/activities/:id/my-invitations
 * 点击某行打开「被邀请人详情」抽屉，展示姓名、手机号（可复制）、参加时间、提交内容(extra)、是否中奖
 */
export default function MyInvitationsPage() {
  const { id: activityId } = useParams<{ id: string }>();
  const user = useAppSelector((state) => state.auth.user);
  type TabKind = 'all' | 'winners';
  const [tab, setTab] = React.useState<TabKind>('all');
  const [currentPageAll, setCurrentPageAll] = React.useState(1);
  const [currentPageWinners, setCurrentPageWinners] = React.useState(1);
  const pageSize = 20;
  const [selectedItem, setSelectedItem] = React.useState<InvitationItem | null>(null);
  const detailDrawer = useOverlayState({ defaultOpen: false });
  const [posterOpen, setPosterOpen] = React.useState(false);
  const [posterDataUrl, setPosterDataUrl] = React.useState<string | null>(null);
  const [posterGenerating, setPosterGenerating] = React.useState(false);
  const trpcUtils = trpc.useUtils();

  const WINNERS_ALERT_STORAGE_KEY = 'my-invitations-winners-alert-dismissed';
  const [showWinnersAlert, setShowWinnersAlert] = React.useState(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem(WINNERS_ALERT_STORAGE_KEY) !== 'true';
  });
  const handleDismissWinnersAlert = React.useCallback(() => {
    try {
      window.localStorage.setItem(WINNERS_ALERT_STORAGE_KEY, 'true');
    } catch {
      // ignore
    }
    setShowWinnersAlert(false);
  }, []);

  // 活动详情（用于海报二维码 URL）
  const { data: activity } = trpc.activities.get.useQuery(
    { id: activityId! },
    { enabled: !!activityId }
  );

  // 共邀请（全部）
  const { data: dataAll, isLoading: loadingAll } = trpc.activityRegistrations.listMyInvitations.useQuery(
    {
      activityId: activityId!,
      page: currentPageAll,
      pageSize,
    },
    { enabled: !!activityId && !!user?.id }
  );

  // 已中奖（isWinner=true）
  const { data: dataWinners, isLoading: loadingWinners } = trpc.activityRegistrations.listMyInvitations.useQuery(
    {
      activityId: activityId!,
      page: currentPageWinners,
      pageSize,
      isWinner: true,
    },
    { enabled: !!activityId && !!user?.id }
  );

  const totalAll = dataAll?.total ?? 0;
  const totalWinners = dataWinners?.total ?? 0;
  const data = tab === 'all' ? dataAll : dataWinners;
  const isLoading = tab === 'all' ? loadingAll : loadingWinners;
  const total = data?.total ?? 0;
  const items = data?.items ?? [];
  const hasNext = data?.hasNext ?? false;
  const hasPrev = data?.hasPrev ?? false;
  const currentPage = tab === 'all' ? currentPageAll : currentPageWinners;
  const setCurrentPage = tab === 'all' ? setCurrentPageAll : setCurrentPageWinners;

  const totalPages = Math.max(1, data?.totalPages ?? 1);
  const paginationPages = useMemo(
    () => buildPaginationPages(totalPages, currentPage),
    [totalPages, currentPage]
  );

  const handleSegmentChange = React.useCallback((key: React.Key) => {
    const k = String(key);
    if (k === 'all' || k === 'winners') setTab(k as TabKind);
  }, []);

  const handleRowClick = (item: InvitationItem) => {
    setSelectedItem(item);
    detailDrawer.open();
  };

  const drawWinnerPoster = useCallback(async (item: InvitationItem): Promise<string> => {
    const canvas = document.createElement('canvas');
    canvas.width = POSTER_WIDTH;
    canvas.height = POSTER_HEIGHT;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2d not available');

    const bgImg = new Image();
    const bgSrc = typeof postbgUrl === 'string' ? postbgUrl : (postbgUrl as { default?: string }).default ?? '';
    await new Promise<void>((resolve, reject) => {
      bgImg.onload = () => resolve();
      bgImg.onerror = () => reject(new Error('背景图加载失败'));
      bgImg.src = bgSrc;
    });
    ctx.drawImage(bgImg, 0, 0, POSTER_WIDTH, POSTER_HEIGHT);

    let prizeImageUrl = item.prize?.imageUrl?.trim() || '';
    if (!prizeImageUrl && item.prizeId) {
      try {
        const prizeDetail = await trpcUtils.activityPrizes.get.fetch({ id: item.prizeId });
        prizeImageUrl = prizeDetail?.imageUrl?.trim() || '';
      } catch {
        // 忽略兜底查询错误，保持静默降级
      }
    }
    if (prizeImageUrl) {
      try {
        const prizeImg = await loadCrossOriginImage(prizeImageUrl);
        const px = 342;
        const py = 1066;
        const pw = 397;
        const ph = 532;
        // 固定高度 532，宽度按原图等比缩放，并在奖品区域内居中（超出区域宽度时会被裁切）
        const drawHeight = 532;
        const drawWidth = (prizeImg.width / prizeImg.height) * drawHeight;
        const dx = px + (pw - drawWidth) / 2;
        const dy = py + (ph - drawHeight) / 2;
        const rr = Math.min(24, drawWidth / 2, drawHeight / 2);
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(dx + rr, dy);
        ctx.lineTo(dx + drawWidth - rr, dy);
        ctx.quadraticCurveTo(dx + drawWidth, dy, dx + drawWidth, dy + rr);
        ctx.lineTo(dx + drawWidth, dy + drawHeight - rr);
        ctx.quadraticCurveTo(dx + drawWidth, dy + drawHeight, dx + drawWidth - rr, dy + drawHeight);
        ctx.lineTo(dx + rr, dy + drawHeight);
        ctx.quadraticCurveTo(dx, dy + drawHeight, dx, dy + drawHeight - rr);
        ctx.lineTo(dx, dy + rr);
        ctx.quadraticCurveTo(dx, dy, dx + rr, dy);
        ctx.closePath();
        ctx.clip();
        ctx.drawImage(prizeImg, dx, dy, drawWidth, drawHeight);
        ctx.restore();
      } catch (err) {
        console.warn('[海报] 奖品图片加载失败，已跳过', err);
      }
    }

    const centerX = 540;
    const fontFamily = 'system-ui, "PingFang SC", "Microsoft YaHei", sans-serif';
    const textColor = '#f6fadf';

    const drawTextAt = (y: number, content: string, fontSize: number, bold = false) => {
      ctx.font = `${bold ? 'bold ' : ''}${fontSize}px ${fontFamily}`;
      ctx.fillStyle = textColor;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText(content, centerX, y);
    };

    drawTextAt(436, '恭喜', 48);
    drawTextAt(520, nameFirstCharWithGender(item.name, item.gender), 118, true);
    drawTextAt(656, `手机号：${maskMobile(item.mobile)}`, 36);
    drawTextAt(743, '获得', 48);
    const prizeLabel = `${(item.prize?.name ?? '').trim() || '奖品'}1份`;
    drawTextAt(827, prizeLabel, 60, true);
    drawTextAt(936, `中奖时间：${formatPosterWonAt(item.wonAt)}`, 36);
    drawTextAt(988, `参与活动：${truncatePosterTitle(activity?.title, 18)}`, 36);

    // 海报底部：当前用户头像、昵称、空间名称（来自 auth 缓存）
    const currentUser = user;
    const avatarSize = 106;
    const avatarX = 58;
    const avatarY = 1760;
    const leftTextX = 178;
    const nicknameY = 1774;
    const spaceNameY = 1832;

    const defaultAvatarSrc =
      typeof defaultAvatarUrl === 'string' ? defaultAvatarUrl : (defaultAvatarUrl as { default?: string }).default ?? '';
    const avatarImageUrl = (currentUser?.avatarUrl?.trim() || defaultAvatarSrc);
    if (avatarImageUrl) {
      try {
        const avatarImg = await loadCrossOriginImage(avatarImageUrl);
        ctx.save();
        ctx.beginPath();
        ctx.arc(avatarX + avatarSize / 2, avatarY + avatarSize / 2, avatarSize / 2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
        ctx.drawImage(avatarImg, avatarX, avatarY, avatarSize, avatarSize);
        ctx.restore();
      } catch (err) {
        console.warn('[海报] 头像加载失败，已跳过', err);
      }
    }

    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillStyle = textColor;
    // 昵称（用户名）宽度
    ctx.font = `44px ${fontFamily}`;
    const nicknameText = (currentUser?.nickname?.trim() || currentUser?.username || '').slice(0, 20);
    const nicknameWidth = ctx.measureText(nicknameText).width;
    // 空间名称宽度，与昵称取大，保证竖线和「祝贺」在左侧文字块右侧统一对齐
    ctx.font = `22px ${fontFamily}`;
    const spaceNameText = (currentUser?.spaces?.[0]?.name ?? '').slice(0, 24);
    const spaceNameWidth = ctx.measureText(spaceNameText).width;
    const leftBlockWidth = Math.max(nicknameWidth, spaceNameWidth);
    const gapBeforeLine = 32;
    const lineWidth = 2;
    const gapAfterLine = 32;
    const verticalLineX = leftTextX + leftBlockWidth + gapBeforeLine;
    const congratsX = verticalLineX + lineWidth + gapAfterLine;

    ctx.fillStyle = '#f6fadf';
    ctx.fillRect(verticalLineX, 1760, lineWidth, 106);
    ctx.fillStyle = textColor;
    ctx.font = `44px ${fontFamily}`;
    ctx.fillText(nicknameText, leftTextX, nicknameY);
    ctx.font = `78px ${fontFamily}`;
    ctx.fillStyle = '#f6fadf';
    ctx.fillText('祝贺', congratsX, 1774);
    ctx.font = `22px ${fontFamily}`;
    ctx.fillStyle = textColor;
    ctx.fillText(spaceNameText, leftTextX, spaceNameY);
    ctx.textAlign = 'center';

    // 二维码：与 ActivityPostersPage 一致的 URL，左上 (878,1730)，140×140，前景 #000000，背景 #f6fadf
    const qrcodeLeft = 878;
    const qrcodeTop = 1730;
    const qrcodeSize = 140;
    const mobileUrl = import.meta.env.VITE_MOBILE_URL || (typeof window !== 'undefined' ? window.location.origin : '');
    const onlineLink = activity?.onlineLink ?? '';
    const path = onlineLink.startsWith('/') ? onlineLink : onlineLink ? `/${onlineLink}` : '';
    const qrcodeUrl = activityId && user?.id ? `${mobileUrl}${path}?activity=${activityId}&inviter=${user.id}` : '';
    if (qrcodeUrl) {
      try {
        const qrDataUrl = await QRCode.toDataURL(qrcodeUrl, {
          width: qrcodeSize,
          margin: 1,
          color: { dark: '#000000', light: '#f6fadf' },
        });
        const qrImg = new Image();
        await new Promise<void>((resolve, reject) => {
          qrImg.onload = () => resolve();
          qrImg.onerror = () => reject(new Error('二维码图片加载失败'));
          qrImg.src = qrDataUrl;
        });
        ctx.drawImage(qrImg, qrcodeLeft, qrcodeTop, qrcodeSize, qrcodeSize);
      } catch {
        // 二维码生成失败时跳过
      }
    }

    return canvas.toDataURL('image/png');
  }, [user, activity, activityId, trpcUtils]);

  useEffect(() => {
    if (!posterOpen || !selectedItem) {
      if (!posterOpen) setPosterDataUrl(null);
      return;
    }
    setPosterGenerating(true);
    setPosterDataUrl(null);
    drawWinnerPoster(selectedItem)
      .then((dataUrl) => {
        setPosterDataUrl(dataUrl);
      })
      .catch((err) => {
        toast.danger(err instanceof Error ? err.message : '生成喜报失败');
      })
      .finally(() => {
        setPosterGenerating(false);
      });
  }, [posterOpen, selectedItem, drawWinnerPoster]);

  const { copy } = useClipboard({
    onSuccess: () => toast.success('已复制，可以去粘贴了'),
    onError: () => toast.danger('复制失败'),
  });

  return (
    <>
      <NavBar
        title="我的邀请"
        showBack
      />
      <div className="flex flex-col h-full bg-background pt-[var(--navbar-height,44px)]">
        {isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-4">
            <div className="flex items-center justify-center mb-4">
              <Segment
                selectedKey={tab}
                onSelectionChange={handleSegmentChange}
              >
                <Segment.Item id="all">
                  <Segment.Separator />
                  {`共邀请${totalAll}人`}
                </Segment.Item>
                <Segment.Item id="winners">
                  <Segment.Separator />
                  {`已中奖${totalWinners}人`}
                </Segment.Item>
              </Segment>
            </div>

            {showWinnersAlert && (
              <Alert status="accent" className="mb-4">
                <AlertTitle>新增功能提示</AlertTitle>
                <AlertDescription>
                  可直接筛选已中奖人员，点击上方「已中奖」即可。
                </AlertDescription>
                <div className="mt-3">
                  <Button variant="primary" size="sm" onClick={handleDismissWinnersAlert}>
                    知道了
                  </Button>
                </div>
              </Alert>
            )}

            <div className="space-y-2">
              {items.length === 0 ? (
                <div className="py-8 text-center text-default-500">暂无邀请记录</div>
              ) : (
                items.map((item) => {
                  const isWon = item.isWinner === true;
                  const attendedAt = formatDateTime(item.createdAt ?? item.registeredAt) || '-';

                  return (
                    <ItemCard
                      key={item.id}
                      className="cursor-pointer transition-colors hover:bg-default/30 shadow-none"
                      role="button"
                      tabIndex={0}
                      onClick={() => handleRowClick(item as InvitationItem)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleRowClick(item as InvitationItem);
                        }
                      }}
                    >
                      <ItemCard.Icon>
                        <span className="flex size-9 items-center justify-center text-sm font-semibold">
                          {nameFirstGrapheme(item.name)}
                        </span>
                      </ItemCard.Icon>
                      <ItemCard.Content>
                        <ItemCard.Title>{nameSpaceGenderTitle(item.name, item.gender)}</ItemCard.Title>
                        <ItemCard.Description>{attendedAt}</ItemCard.Description>
                      </ItemCard.Content>
                      <ItemCard.Action>
                        <div className="flex items-center gap-1.5">
                          {isWon ? <span className="text-sm text-danger">恭喜中奖</span> : null}
                          <ChevronRight className="text-muted size-4 shrink-0" />
                        </div>
                      </ItemCard.Action>
                    </ItemCard>
                  );
                })
              )}
            </div>

            {total > 0 ? (
              <Pagination className="mt-6 justify-center" size="sm">
                <Pagination.Content>
                  <Pagination.Item>
                    <Pagination.Previous
                      isDisabled={!hasPrev}
                      onPress={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    >
                      <Pagination.PreviousIcon />
                      <span>上一页</span>
                    </Pagination.Previous>
                  </Pagination.Item>
                  {paginationPages.map((p, idx) =>
                    p === 'ellipsis' ? (
                      <Pagination.Item key={`ellipsis-${idx}`}>
                        <Pagination.Ellipsis />
                      </Pagination.Item>
                    ) : (
                      <Pagination.Item key={p}>
                        <Pagination.Link
                          isActive={p === currentPage}
                          onPress={() => {
                            setCurrentPage(p);
                          }}
                        >
                          {p}
                        </Pagination.Link>
                      </Pagination.Item>
                    )
                  )}
                  <Pagination.Item>
                    <Pagination.Next
                      isDisabled={!hasNext}
                      onPress={() => setCurrentPage((p) => p + 1)}
                    >
                      <span>下一页</span>
                      <Pagination.NextIcon />
                    </Pagination.Next>
                  </Pagination.Item>
                </Pagination.Content>
              </Pagination>
            ) : null}
          </div>
        )}
      </div>

      {/* 被邀请人详情：Backdrop → Content → Dialog；受控用 state（勿在 Content 上 max-h 截断 fixed 层，否则底部会留白） */}
      <Drawer state={detailDrawer}>
        <Drawer.Trigger className="sr-only">被邀请人详情</Drawer.Trigger>
        <Drawer.Backdrop>
          <Drawer.Content placement="bottom">
            <Drawer.Dialog className="flex min-h-0 flex-col bg-background text-foreground">
              <Drawer.CloseTrigger aria-label="关闭" />
              <Drawer.Handle />
              <Drawer.Header className="shrink-0 pb-2">
                <Drawer.Heading className="text-lg font-semibold">被邀请人详情</Drawer.Heading>
              </Drawer.Header>
              {selectedItem ? (
                <Drawer.Body className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 pb-[max(0.75rem,env(safe-area-inset-bottom,0px))]">
                  <div>
                    <div className="text-sm text-default-500 mb-1">姓名</div>
                    <div className="text-base text-foreground">
                      {nameSpaceGenderTitle(selectedItem.name, selectedItem.gender)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-default-500 mb-1">手机号</div>
                    <div className="flex items-center gap-2">
                      <span className="text-base text-foreground">{selectedItem.mobile || '-'}</span>
                      {selectedItem.mobile && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            copy(selectedItem.mobile!);
                          }}
                        >
                          复制
                        </Button>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-default-500 mb-1">参加时间</div>
                    <div className="text-base text-foreground">
                      {formatDateTime(selectedItem.createdAt ?? selectedItem.registeredAt) || '-'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-default-500 mb-1">提交内容</div>
                    <div className="text-base text-foreground break-words">
                      {formatExtra(selectedItem.extra) || '-'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-default-500 mb-1">是否中奖</div>
                    <div className="text-base text-foreground">
                      {selectedItem.isWinner ? '已中奖' : '未中奖'}
                    </div>
                  </div>
                  {selectedItem.isWinner && (
                    <>
                      <div>
                        <div className="text-sm text-default-500 mb-1">奖品名称</div>
                        <div className="text-base text-foreground">
                          {selectedItem.prize?.name?.trim() || '-'}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-default-500 mb-1">中奖时间</div>
                        <div className="text-base text-foreground">
                          {formatDateTime(selectedItem.wonAt) || '-'}
                        </div>
                      </div>
                    </>
                  )}
                  {selectedItem.isWinner && (
                    <Button
                      className="mt-4 w-full"
                      variant="primary"
                      onClick={() => {
                        detailDrawer.close();
                        setTimeout(() => setPosterOpen(true), 320);
                      }}
                    >
                      生成中奖喜报
                    </Button>
                  )}
                </Drawer.Body>
              ) : null}
            </Drawer.Dialog>
          </Drawer.Content>
        </Drawer.Backdrop>
      </Drawer>

      {/* 中奖喜报全屏：挂载到 body，与 Drawer Portal 同层堆叠，避免微信 WebView 下被抽屉盖住 */}
      {posterOpen &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex flex-col bg-black/80 items-center justify-center p-4">
            <button
              type="button"
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70"
              onClick={() => setPosterOpen(false)}
              aria-label="关闭"
            >
              <X className="h-6 w-6" />
            </button>
            {posterGenerating ? (
              <div className="flex flex-col items-center gap-4">
                <Spinner className="h-10 w-10 text-white" />
                <span className="text-white text-sm">生成中...</span>
              </div>
            ) : posterDataUrl ? (
              <div className="flex flex-col items-center gap-4 w-full max-w-full overflow-auto">
                <img
                  src={posterDataUrl}
                  alt="中奖喜报"
                  className="max-w-full h-auto rounded-lg shadow-lg relative z-[1]"
                  style={{
                    maxHeight: '75vh',
                    pointerEvents: 'auto',
                    WebkitTouchCallout: 'default',
                    WebkitUserSelect: 'none',
                    userSelect: 'none',
                  }}
                  draggable={false}
                />
                <p className="text-white/90 text-sm text-center">
                  微信内长按图片保存到相册 · 电脑端右键图片另存为
                </p>
              </div>
            ) : null}
          </div>,
          document.body
        )}
    </>
  );
}
